<script setup lang="ts">
    import { Button } from '@/components/ui/button';
    import {
        Dialog,
        DialogContent,
        DialogDescription,
        DialogHeader,
        DialogTitle,
        DialogClose,
        DialogFooter,
        DialogTrigger,
        DialogBody,
    } from '@/components/ui/dialog';
    import { Alert } from '@/components/ui/alert';
    import { computed, ref } from 'vue';
    import { Plus } from 'lucide-vue-next';
    import { toast } from 'vue-sonner';
    import { useI18n } from 'vue-i18n';
    import { usePermissions } from '@/composables/usePermissions';
    import DataTableActions from '@/components/datatable/DataTableActions.vue';
    import FileUploadZone from '../FileUploadZone.vue';
    import Permission from '@/enums/Permission';
    import type { HolidayPayObligation } from '@/types';

    const { hasPermission } = usePermissions();

    const props = defineProps<{
        holidayPayObligation: HolidayPayObligation;
        modelValue?: boolean;
    }>();

    const emit = defineEmits<{
        'update:modelValue': [value: boolean];
    }>();

    const open = computed({
        get: () => props.modelValue ?? false,
        set: (value) => emit('update:modelValue', value),
    });

    const uploadedFile = ref([]);

    const { t } = useI18n();

    const handleSuccess = () => {
        toast.success(t('forms.general.success'));
    };
</script>

<template>
    <DataTableActions
        v-if="hasPermission(Permission.HOLIDAY_PAY_OBLIGATIONS_UPDATE)"
        :data="holidayPayObligation"
    >
        <Dialog>
            <DialogTrigger as-child>
                <Button
                    variant="ghost"
                    size="xs"
                    width="narrow"
                >
                    {{ t('forms.upload') }}
                    <Plus class="text-accent size-3" />
                </Button>
            </DialogTrigger>
            <DialogContent class="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>
                        {{ t('holiday-pay-obligations.upload') }}
                    </DialogTitle>
                </DialogHeader>

                <DialogBody class="space-y-5">
                    <DialogDescription>
                        {{ t('holiday-pay-obligations.file_headers_needed') }}
                    </DialogDescription>

                    <Alert variant="warning">
                        {{ t('holiday-pay-obligations.file_import_warning') }}
                    </Alert>

                    <FileUploadZone
                        v-model="uploadedFile"
                        instant-upload
                        :accepts-files="['.xls', '.xlsx']"
                        :route="
                            route('v2.salary.holiday-pay-obligations.upload', {
                                companyId: holidayPayObligation.company_id,
                                id: holidayPayObligation.payroll_id,
                            })
                        "
                        :max-uploads="1"
                        @success="handleSuccess"
                    />
                </DialogBody>

                <DialogFooter class="gap-2 border-t sm:justify-center">
                    <DialogClose as-child>
                        <Button
                            variant="outline"
                            size="sm"
                        >
                            {{ t('forms.close') }}
                        </Button>
                    </DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    </DataTableActions>
</template>
